import { useAsync } from 'react-use';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

const DAAS_PROJECT_TYPE = 'daas-project';
const DAAS_APPLICATION_TYPE = 'daas-application';
const DAAS_APPLICATION_SET_TYPE = 'daas-application-set';

/**
 * Maps a DaaS entity's `spec.type` to the `daas-backend` "list editable"
 * endpoint segment that authorizes it. Any other type is not an editable DaaS
 * entity and short-circuits without a backend round-trip.
 */
const EDITABLE_LIST_PATH_BY_TYPE: Record<string, string> = {
  [DAAS_PROJECT_TYPE]: 'projects',
  [DAAS_APPLICATION_TYPE]: 'applications',
  [DAAS_APPLICATION_SET_TYPE]: 'application-sets',
};

/**
 * Hook that checks whether the current user is allowed to edit or delete a DaaS
 * entity.
 *
 * @param entity - The DaaS entity (project, application, or application-set)
 * @returns `isSecurityGroupMember` - whether the user may edit and delete this
 *   entity, `loading` - whether the check is still in progress, and `error`
 *   any error raised while fetching the decision (fails closed:
 *   `isSecurityGroupMember` is `false` while loading or on error).
 */
export const useDaaSEditPermission = (
  entity: Entity,
): {
  isSecurityGroupMember: boolean;
  loading: boolean;
  error: Error | undefined;
} => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const entityType = entity.spec?.type as string | undefined;
  const listPath = entityType
    ? EDITABLE_LIST_PATH_BY_TYPE[entityType]
    : undefined;
  const entityRef = stringifyEntityRef(entity);

  const { value, loading, error } = useAsync(async () => {
    // Not a DaaS entity kind we can authorize - deny without a backend call.
    if (!listPath) {
      return false;
    }

    const baseUrl = await discoveryApi.getBaseUrl('daas');
    const response = await fetchApi.fetch(
      `${baseUrl}/permissions/authorize/${listPath}`,
    );

    if (!response.ok) {
      throw new Error(
        `Failed to load DaaS edit permissions: ${response.status} ${response.statusText}`,
      );
    }

    const data = (await response.json()) as { entityRefs?: string[] };
    const editable = new Set(data.entityRefs ?? []);
    return editable.has(entityRef);
  }, [listPath, entityRef]);

  return {
    isSecurityGroupMember: value ?? false,
    loading,
    error,
  };
};
